import React from 'react';
import { DashboardSidebarView } from './DashboardSidebar';
import { useSidebar } from '../contexts/SidebarContext';

/**
 * AppShell — the persistent chrome around the routed page.
 *
 * Rendered once in App.js, as a sibling of <Navigation/> and OUTSIDE <Routes>,
 * so nothing in here unmounts when the route changes.
 *
 * Why this exists: every dashboard page used to render its own
 * <DashboardSidebar/>. Because the page component unmounts on navigation, so
 * did the sidebar — re-running its three startup fetches (team invitations,
 * media storage, AI image usage) on every single page view. Measured on a real
 * page load that came to ~22 requests, with /media-library/storage alone firing
 * five times. The production rate limit is 100/min, so a host clicking around
 * could trip it and get 429s on ordinary navigation.
 *
 * Visibility, not mounting, is what toggles here. `sidebarEverRequested`
 * latches on first use and never resets, because during a page→page navigation
 * React unmounts the outgoing page before mounting the incoming one — so the
 * live request count dips to 0 in between. Unmounting on that dip would
 * refetch and reset scroll, which is the whole thing we're removing. Hiding
 * with the `hidden` attribute keeps the DOM node (and its scroll position)
 * intact.
 *
 * The sidebar is `position: fixed`, so moving it out of the page's DOM subtree
 * has no layout effect — pages keep their own content offset.
 */
const AppShell = () => {
  const { sidebarVisible, sidebarEverRequested } = useSidebar();

  // Nothing has ever asked for the sidebar (e.g. a logged-out visitor on the
  // marketing site) — render nothing at all rather than hidden markup.
  if (!sidebarEverRequested) return null;

  return (
    <div hidden={!sidebarVisible} data-testid="app-shell-sidebar">
      <DashboardSidebarView />
    </div>
  );
};

export default AppShell;
